import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useAgencySelector from '@hooks/useAgencySelector'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as assetLibraryActions from '@redux/modules/assetLibrary'

import defaultFormState, { requiredFields } from '@models/assetLibrary'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { AssetLibraryModel, AssetLibraryRequestOptions } from '@models/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (assetLibrary?: Partial<AssetLibraryModel>, options: GenerateUrlsOptions = {}) => {
  const { id } = assetLibrary || {}
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    assetLibrariesIndexUrl: `#/${prefixRoute}/assetLibraries`,
    editAssetLibraryDetailsUrl: `#/${prefixRoute}/assetLibraries/${id}/edit/details`,
    editAssetLibraryUrl: `#/${prefixRoute}/assetLibraries/${id}/edit/`,
  }
}

type CreateAssetLibraryParams = {
  assetLibraryParams: Partial<AssetLibraryModel>,
  dispatch: AppDispatch,
  requestOptions?: AssetLibraryRequestOptions,
}

const createAssetLibrary = (params: CreateAssetLibraryParams) => {
  const { assetLibraryParams, dispatch, requestOptions } = params
  const { createAssetLibrary: createFn } = assetLibraryActions

  return dispatch(createFn(assetLibraryParams, requestOptions))
}

type UpdateAssetLibraryParams = {
  assetLibrary: AssetLibraryModel,
  assetLibraryParams: Partial<AssetLibraryModel>,
  dispatch: AppDispatch,
  requestOptions?: AssetLibraryRequestOptions,
}

const updateAssetLibrary = (params: UpdateAssetLibraryParams) => {
  const {
    assetLibrary, assetLibraryParams, dispatch, requestOptions,
  } = params
  const { updateAssetLibrary: updateFn } = assetLibraryActions

  const updatedParams = {
    id: assetLibrary.id,
    ...assetLibraryParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAssetLibraryParams = {
  assetLibrary: DeleteParams<AssetLibraryModel>,
  dispatch: AppDispatch,
}

const deleteAssetLibrary = (params: DeleteAssetLibraryParams) => {
  const { assetLibrary, dispatch } = params
  const { deleteAssetLibrary: deleteFn } = assetLibraryActions

  return dispatch(deleteFn(assetLibrary))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAssetLibraryForm(
  assetLibrary: Partial<AssetLibraryModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const assetLibraryForm = useForm(
    defaultFormState,
    { entity: assetLibrary, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [assetLibrary.id, assetLibrary.cache_key],
  )

  return {
    ...assetLibraryForm,
  }
}

export const useRelations = (assetLibrary: Partial<AssetLibraryModel> = {}) => {
  const { owner_id, owner_type } = assetLibrary

  const { agencies, brands } = useSelector(reduxState => reduxState.entities)

  const agency = owner_type === 'Agency' && owner_id && agencies[owner_id] ? agencies[owner_id] : {}
  const brand = owner_type === 'Brand' && owner_id && brands[owner_id] ? brands[owner_id] : {}

  return {
    agency,
    brand,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AssetLibraryRequestOptions,
}

function useAssetLibrary(initEntity: Partial<AssetLibraryModel> = {}, options: Options = {}) {
  const {
    performHttpRequests, requestOptions,
  } = options

  // @ts-expect-error TS2322
  const { entity: assetLibrary }: { entity: AssetLibraryModel } = useLatestEntity(initEntity, 'assetLibraries')
  const { id, owner_id, owner_type } = assetLibrary

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, brand } = useRelations(assetLibrary)

  useReduxAction(
    'assetLibraries',
    'loadAssetLibrary',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.assetLibraries)

  const { selectedAgencyId } = useAgencySelector()
  const { isAdmin, isAgencyAdmin, isBrandUser } = useCurrentUser()
  const isCurrentAgencyLibrary = isAgencyAdmin && owner_id === selectedAgencyId && owner_type === 'Agency'
  const editable = isAdmin || isBrandUser || isCurrentAgencyLibrary

  return {
    agency,
    assetLibrary,
    brand,
    callbacks: {
      createAssetLibrary: (
        assetLibraryParams: Partial<AssetLibraryModel>,
        entityOptions?: AssetLibraryRequestOptions,
      ) => (
        createAssetLibrary({ assetLibraryParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAssetLibrary: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAssetLibraryModal',
        payload: { assetLibrary },
      }),
      deleteAssetLibrary: () => deleteAssetLibrary({ dispatch, assetLibrary }),
      updateAssetLibrary: (
        assetLibraryParams: Partial<AssetLibraryModel>,
        entityOptions?: AssetLibraryRequestOptions,
      ) => (
        updateAssetLibrary({
          assetLibrary, assetLibraryParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    editable,
    loading: loading && !loadedIds.includes(assetLibrary.id),
    updating,
    urls: generateUrls(assetLibrary),
  }
}

export default useAssetLibrary
